import { ScreenReaderContent } from "@instructure/ui";
import { t } from "i18next";

/**
 * Transforms graph data from the assessment results API into the 2D array
 * format expected by SimpleTable, along with caption and rowTitle props.
 *
 * @param {Array} graphData - [results, categories] where:
 *   - results is an array of objects: [{ name: "LO Name", "Term1": 73.45, "Term2": null, ... }, ...]
 *   - categories is a sorted array of term/course names: ["Fall 2024", "Spring 2025", ...]
 * @param {string} measureTitle - The title to use as the table caption
 * @returns {{ data: Array[], caption: string, rowTitle: string }}
 */
export function transformResultsToTableData(graphData, measureTitle) {
  const [results, categories] = graphData;

  const headerRow = ["", ...categories];

  // Group by name and merge values (the API returns one object per
  // outcome/term combination, so multiple objects share the same name).
  const merged = new Map();
  results.forEach((item) => {
    if (!merged.has(item.name)) {
      merged.set(item.name, {});
    }
    const existing = merged.get(item.name);
    categories.forEach((category) => {
      if (item[category] != null) {
        existing[category] = item[category];
      }
    });
  });

  const dataRows = [...merged.entries()].map(([name, values]) => {
    const row = [name];
    categories.forEach((category) => {
      const value = values[category];
      if (value == null) {
        row.push(
          <>
            &ndash;
            <ScreenReaderContent>
              {t("results.noDataAvailable")}
            </ScreenReaderContent>
          </>
        );
      } else {
        row.push(`${Math.round(value * 100) / 100}%`);
      }
    });
    return row;
  });

  return {
    data: [headerRow, ...dataRows],
    caption: measureTitle,
    rowTitle: t("ratingScale.lo"),
  };
}
